// covered-by: frontends/carrie/src/ask-v2/review/__tests__/AskV2ReviewCommentsList.test.tsx
import { CaretDown, Funnel } from "@phosphor-icons/react";
import { Badge, Checkbox, Popover, Text } from "@radix-ui/themes";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import {
  activeFilterCount,
  AskV2ReviewFilterMenuFooter,
  AskV2ReviewGroupBySection,
  AskV2ReviewStatusFilterSection,
  MenuHeading,
  type ReviewListGroupByControl,
  type ReviewListPinGrouping,
  type ReviewListShowMinorControl,
} from "./AskV2ReviewFilterMenuParts";
import {
  NO_HIDDEN_SOURCES,
  REVIEW_COMMENT_TAG_IDS,
  type ReviewCommentSourceKey,
  type ReviewCommentTagId,
  isReviewCommentFilterActive,
  reviewCommentTagLabelKey,
} from "./reviewCommentTags";
import { type ReviewListStatusFilter } from "./useReviewListView";

/** One "raised by" entry: Puntt's own findings, or one expert's review. */
export type ReviewSourceFilterOption = {
  key: ReviewCommentSourceKey;
  label: string;
};

type AskV2ReviewTagFilterProps = {
  activeTags: ReadonlySet<ReviewCommentTagId>;
  onToggle: (tag: ReviewCommentTagId, checked: boolean) => void;
  alwaysOn?: boolean;
  onToggleAlwaysOn?: (checked: boolean) => void;
  /** Rendered only when an expert's method raised a finding in this review. */
  sources?: readonly ReviewSourceFilterOption[];
  hiddenSources?: ReadonlySet<ReviewCommentSourceKey>;
  onToggleSource?: (source: ReviewCommentSourceKey, checked: boolean) => void;
  /** Group by, at the top of the menu; absent, the menu filters only. */
  groupBy?: ReviewListGroupByControl;
  /**
   * The Status boxes. Given (flag `ask-v2-review-status-tabs`), this is the
   * one Filter menu: a "Filter" button with a count, Show minor and Reset.
   */
  statusFilter?: ReviewListStatusFilter;
  /** The pin view's Show minor switch; absent while pin groups are off. */
  showMinor?: ReviewListShowMinorControl;
  /** Puts every box back: types, Always-on, raisers, statuses, Show minor. */
  onReset?: () => void;
  /** The pin view's block and place groups, offered in Group by. */
  pinGrouping?: ReviewListPinGrouping;
};

/** Testid-safe suffix for a source key (`expert:<id>` -> `expert-<id>`). */
function sourceTestId(key: ReviewCommentSourceKey): string {
  return key.replace(/[^a-z0-9-]/gi, "-");
}

function filterTriggerLabel(
  activeTags: ReadonlySet<ReviewCommentTagId>,
  hiddenSources: ReadonlySet<ReviewCommentSourceKey>,
  sourceKeys: readonly ReviewCommentSourceKey[],
  alwaysOn: boolean,
  raisedBySourceCount: number,
  labelFor: (tag: ReviewCommentTagId) => string,
  t: (key: string, options?: Record<string, unknown>) => string,
): string {
  if (
    !isReviewCommentFilterActive(
      activeTags,
      alwaysOn,
      hiddenSources,
      sourceKeys,
    )
  ) {
    return t("askV2.review.tagFilterNone");
  }
  const hiddenSourceCount = sourceKeys.filter((key) =>
    hiddenSources.has(key),
  ).length;
  if (hiddenSourceCount > 0 || !alwaysOn) {
    return t("askV2.review.tagFilterFiltered");
  }
  if (activeTags.size === REVIEW_COMMENT_TAG_IDS.length) {
    return t("askV2.review.tagFilterAll");
  }
  if (activeTags.size === 0) {
    return t("askV2.review.tagFilterNone");
  }
  if (activeTags.size === 1) {
    // why: one category plus Raised by (or Always-on) is still a multi-part
    // filter; naming only the category misreads as a single-type view.
    if (raisedBySourceCount > 0) {
      return t("askV2.review.tagFilterFiltered");
    }
    const only = Array.from(activeTags)[0];
    return only
      ? labelFor(only)
      : t("askV2.review.tagFilterCount", { count: 1 });
  }
  return t("askV2.review.tagFilterCount", { count: activeTags.size });
}

export function AskV2ReviewTagFilter({
  activeTags,
  onToggle,
  alwaysOn = true,
  onToggleAlwaysOn = () => undefined,
  sources = [],
  hiddenSources = NO_HIDDEN_SOURCES,
  onToggleSource = () => undefined,
  groupBy,
  statusFilter,
  showMinor,
  onReset = () => undefined,
  pinGrouping,
}: AskV2ReviewTagFilterProps) {
  const { t } = useTranslation("ui");

  const labelFor = useMemo(
    () => (tag: ReviewCommentTagId) => t(reviewCommentTagLabelKey(tag)),
    [t],
  );

  const sourceKeys = sources.map((source) => source.key);
  const triggerLabel = filterTriggerLabel(
    activeTags,
    hiddenSources,
    sourceKeys,
    alwaysOn,
    sources.length,
    labelFor,
    t,
  );

  const menu = statusFilter != null;
  const filterCount = menu
    ? activeFilterCount({
        activeTags,
        alwaysOn,
        hiddenSources,
        sourceKeys,
        statuses: statusFilter.shown,
        showMinor: showMinor?.value,
      })
    : 0;
  const triggerAriaLabel = !menu
    ? t("askV2.review.tagFilterLabel")
    : filterCount > 0
      ? t("askV2.review.filterLabelActive", { count: filterCount })
      : t("askV2.review.filterLabel");

  return (
    <Popover.Root>
      <Popover.Trigger>
        <button
          type="button"
          className="flex items-center gap-1 rounded-md border border-puntt-neutral-gray-5 bg-base-white px-2 py-1 text-[12px] font-medium text-puntt-neutral-gray-11 hover:bg-puntt-neutral-gray-2"
          data-testid="ask-v2-review-tag-filter"
          aria-label={triggerAriaLabel}
        >
          {menu ? (
            <>
              <Funnel size={14} aria-hidden="true" />
              <span>{t("askV2.review.filterButton")}</span>
              {filterCount > 0 ? (
                <Badge
                  size="1"
                  variant="solid"
                  radius="full"
                  aria-hidden="true"
                  data-testid="ask-v2-review-filter-count"
                >
                  {filterCount}
                </Badge>
              ) : null}
            </>
          ) : (
            <span>{triggerLabel}</span>
          )}
          <CaretDown size={12} aria-hidden="true" />
        </button>
      </Popover.Trigger>
      <Popover.Content
        className="max-h-[min(70vh,420px)] overflow-y-auto p-2"
        data-testid="ask-v2-review-tag-filter-menu"
        aria-label={triggerAriaLabel}
      >
        {groupBy ? (
          <AskV2ReviewGroupBySection
            groupBy={groupBy}
            pinGrouping={pinGrouping}
          />
        ) : null}
        {statusFilter ? (
          <AskV2ReviewStatusFilterSection statusFilter={statusFilter} />
        ) : null}
        {menu ? (
          <MenuHeading>{t("askV2.review.filterCommentTypes")}</MenuHeading>
        ) : null}
        <ul className="space-y-1">
          {REVIEW_COMMENT_TAG_IDS.map((tag) => {
            const checked = activeTags.has(tag);
            const label = labelFor(tag);
            return (
              <li key={tag}>
                <label
                  className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 hover:bg-puntt-neutral-gray-2"
                  data-testid={`ask-v2-review-tag-filter-${tag}`}
                >
                  <Checkbox
                    checked={checked}
                    onCheckedChange={(value) => onToggle(tag, value === true)}
                  />
                  <Text size="2">{label}</Text>
                </label>
              </li>
            );
          })}
        </ul>
        <label
          className={`flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 hover:bg-puntt-neutral-gray-2 ${
            menu ? "mt-1" : "mt-2 border-t border-puntt-neutral-gray-4"
          }`}
          data-testid="review-comment-filter-always-on"
        >
          <Checkbox
            checked={alwaysOn}
            onCheckedChange={(value) => onToggleAlwaysOn(value === true)}
            aria-label={t("askV2.review.alwaysOnChecks")}
          />
          <Text size="2">{t("askV2.review.alwaysOnChecks")}</Text>
        </label>
        {sources.length > 0 ? (
          <section
            className="mt-2 border-t border-puntt-neutral-gray-4 pt-2"
            data-testid="ask-v2-review-source-filter"
            aria-labelledby="ask-v2-review-source-filter-heading"
          >
            <Text
              as="p"
              size="1"
              id="ask-v2-review-source-filter-heading"
              className="px-2 pb-1 text-puntt-neutral-gray-10"
            >
              {t("askV2.review.sourceFilterHeading")}
            </Text>
            <ul className="space-y-1">
              {sources.map((source) => (
                <li key={source.key}>
                  <label
                    className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 hover:bg-puntt-neutral-gray-2"
                    data-testid={`ask-v2-review-source-filter-${sourceTestId(source.key)}`}
                  >
                    <Checkbox
                      checked={!hiddenSources.has(source.key)}
                      onCheckedChange={(value) =>
                        onToggleSource(source.key, value === true)
                      }
                    />
                    <Text size="2">{source.label}</Text>
                  </label>
                </li>
              ))}
            </ul>
          </section>
        ) : null}
        {menu ? (
          <AskV2ReviewFilterMenuFooter
            showMinor={showMinor}
            canReset={filterCount > 0}
            onReset={onReset}
          />
        ) : null}
      </Popover.Content>
    </Popover.Root>
  );
}
